import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Alert, Cell, Grid, Heading, HFlow, InfoLabel, Text, VFlow } from 'bold-ui'
import { LoadingIndicator } from 'components/loading'
import { useHistoricoVisitaDomiciliarQuery } from 'graphql/hooks.generated'
import { OrigemDadoEnum } from 'graphql/types.generated'
import { Fragment, memo } from 'react'
import { stringGrouper } from 'util/string/string-grouper'
import { isEmpty } from 'util/validation/Util'
import { HistoricoDwPanelProps } from 'view/atendimentos/detail/historico/model/model-historico'
import {
  mountInfosAdministrativas,
  naoRegistradoNesseAtendimento,
} from 'view/atendimentos/detail/historico/utils/utils-historico'

import HistoricoDetailTable from '../../../table/HistoricoDetailTable'
import HistoricoGapPanel from '../HistoricoGapPanel'
import { HistoricoIvcfTableBox } from '../HistoricoIvcfTableBox'
import HistoricoNoDetailAvailable from '../HistoricoNoDetailAvailable'

function HistoricoVisitaDomiciliarPanel({
  uuidRegistro,
  cpfOuCnsCidadao,
  idadeOrigemAtendimento,
}: Readonly<HistoricoDwPanelProps>) {
  const {
    data: { historicoVisitaDomiciliar: detalhamento },
    loading,
  } = useHistoricoVisitaDomiciliarQuery({
    variables: {
      input: {
        uuidRegistro,
        cpfOuCnsCidadao,
        isOrigemPec: false,
      },
    },
  })

  if (loading) return <LoadingIndicator />

  if (!detalhamento) return <HistoricoNoDetailAvailable />

  const hasIvcf = !isEmpty(detalhamento.ivcf)
  const hasMedicao: boolean =
    !isEmpty(detalhamento.peso) ||
    !isEmpty(detalhamento.altura) ||
    !isEmpty(detalhamento.temperatura) ||
    !isEmpty(detalhamento.medicaoGlicemia) ||
    !isEmpty(detalhamento.medicaoPressaoArterial) ||
    !isEmpty(detalhamento.dimensaoTipoGlicemia)

  const showHistoricoDetailTable =
    !isEmpty(detalhamento.motivoVisita) ||
    !isEmpty(detalhamento.buscaAtiva) ||
    !isEmpty(detalhamento.acompanhamento) ||
    !isEmpty(detalhamento.controleAmbiental)

  return (
    <HistoricoGapPanel
      idadeOrigemAtendimento={idadeOrigemAtendimento}
      informacoesAdministrativas={mountInfosAdministrativas(detalhamento)}
      tipoVisita={detalhamento.tipoVisita}
    >
      {detalhamento.stVisitaCompartilhada && (
        <Alert type='info' inline>
          <Text color='primary' fontWeight='bold'>
            Visita compartilhada com outro profissional
          </Text>
        </Alert>
      )}

      {showHistoricoDetailTable && (
        <HistoricoDetailTable title='Motivo da visita' sectionStyle='I'>
          <VerticalWrapper>
            <Text>{stringGrouper(...detalhamento.motivoVisita)}</Text>
            {!isEmpty(detalhamento.buscaAtiva) && (
              <InfoLabel title='Busca ativa'>{stringGrouper(...detalhamento.buscaAtiva)}</InfoLabel>
            )}
            {!isEmpty(detalhamento.acompanhamento) && (
              <InfoLabel title='Acompanhamento'>{stringGrouper(...detalhamento.acompanhamento)}</InfoLabel>
            )}
            {!isEmpty(detalhamento.controleAmbiental) && (
              <InfoLabel title='Controle ambiental/vetorial'>
                {stringGrouper(...detalhamento.controleAmbiental)}
              </InfoLabel>
            )}
          </VerticalWrapper>
        </HistoricoDetailTable>
      )}
      {(hasMedicao || hasIvcf) && (
        <VFlow vSpacing={0.5}>
          <HistoricoDetailTable title='Antropometria, sinais vitais e glicemia capilar' sectionStyle='I'>
            <Grid>
              {!hasMedicao && (
                <Cell size={12}>
                  <Text fontStyle='italic'>{naoRegistradoNesseAtendimento}</Text>
                </Cell>
              )}
              {detalhamento.peso && (
                <Cell size={2}>
                  <InfoLabel title='Peso'>{detalhamento.peso} kg</InfoLabel>
                </Cell>
              )}
              {detalhamento.altura && (
                <Cell size={2}>
                  <InfoLabel title='Altura'>{detalhamento.altura} cm</InfoLabel>
                </Cell>
              )}
              {detalhamento.temperatura && (
                <Cell size={2}>
                  <InfoLabel title='Temperatura'>{detalhamento.temperatura} ºC</InfoLabel>
                </Cell>
              )}
              {detalhamento.medicaoPressaoArterial && (
                <Cell size={2}>
                  <InfoLabel title='Pressão arterial'>{detalhamento.medicaoPressaoArterial} mmHg</InfoLabel>
                </Cell>
              )}
              {detalhamento.medicaoGlicemia && (
                <Cell size={4}>
                  <HFlow>
                    <InfoLabel title='Glicemia capilar'>{detalhamento.medicaoGlicemia} mg/dL</InfoLabel>
                    <Text>(Momento da coleta {detalhamento.dimensaoTipoGlicemia.descricao})</Text>
                  </HFlow>
                </Cell>
              )}
            </Grid>
          </HistoricoDetailTable>
          {hasIvcf && (
            <HistoricoIvcfTableBox
              ivcf={detalhamento.ivcf}
              riscoVulnerabilidadeText={detalhamento.ivcf.dimensaoGrauVulnerabilidadeIvcf.descricao}
            />
          )}
        </VFlow>
      )}
      <VFlow vSpacing={0.5}>
        <VFlow vSpacing={0}>
          <Heading level={3}>Desfecho</Heading>
          <Text>{detalhamento.dimensaoDesfechoVisita.descricao}</Text>
        </VFlow>
        {idadeOrigemAtendimento.origemAtendimento === OrigemDadoEnum.TERRITORIO && (
          <Fragment>
            <Heading level={3}>Anotações</Heading>
            {detalhamento.anotacoes ? (
              <Text
                style={css`
                  overflow-wrap: break-word;
                `}
              >
                {detalhamento.anotacoes}
              </Text>
            ) : (
              <Text fontStyle='italic'>Sem anotações registradas.</Text>
            )}
          </Fragment>
        )}
      </VFlow>
    </HistoricoGapPanel>
  )
}

// TODO (RoyalFlush): Até arrumar o VFlow no bold
const VerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  & > * {
    display: inline-block;
    margin: 0.25rem;
  }
  & > :last-child {
    margin-bottom: 0;
  }
  & > :first-child {
    margin-top: 0;
  }
  &:empty {
    display: none;
  }
`

export default memo(HistoricoVisitaDomiciliarPanel)
